import "date-fns";
import React, { useEffect, useState } from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import Typography from "../typography";
import { ITeam } from "../../types";
import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import Link from "../link";
import Button from "../button";
// import gql from "graphql-tag";
import { injectIntl } from "react-intl";
import messages from "./PageViewTeam.intl";
import { ActionLink } from "../atoms";
import { gql, useMutation } from "@apollo/client";
import SnackBar from "../snackbar";

import useIsManager from "../../hooks/useIsManager";

const END_LEAGUE_MUTATION = gql`
  mutation EndLeague($leagueId: ID!) {
    endLeague(leagueId: $leagueId)
  }
`;

// const useStyles = makeStyles((theme: Theme) =>
//   createStyles({
//     root: {
//       width: "100%",
//       maxWidth: 360,
//       backgroundColor: theme.palette.background.paper
//     },
//     inline: {
//       display: "inline"
//     },
//     margin: {
//       margin: theme.spacing(2)
//     },
//     textField: {
//       marginLeft: theme.spacing(1),
//       marginRight: theme.spacing(1),
//       width: 200
//     }
//   })
// );

const CardWrapper = styled(Card)(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  flexDirection: "column",
  display: "flex",
  height: "100%",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(16, 21, 26, 0.08)",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "16px",
  },
}));

const Header = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(2.5, 3),
  borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const Content = styled("div")(({ theme }: IStyledArguments) => ({
  background: "transparent",
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const Title = styled(Link)(({ theme }: IStyledArguments) => ({
  ...theme.typography.h6,
  marginBottom: 0,
  fontWeight: 700,
  color: theme.palette.primary.main,
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
}));

const Chevron = styled("svg")(({ theme }: IStyledArguments) => ({
  color: theme.palette.primary.main,
  flexShrink: 0,
}));

const Row = styled((props) => <Grid container {...props} />)({
  width: "100%",
  flex: 1,
  justifyContent: "space-between",
});

const DetailLabel = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.68rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: theme.palette.text.secondary,
  marginBottom: 3,
}));

const DetailValue = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.9rem",
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

interface ITeamSummaryCardProps {
  id: string;
  team: ITeam;
  currentUser: any;
}

const LeagueCard = injectIntl<ITeamSummaryCardProps>(
  ({ id, team, currentUser, intl }) => {
    const [open, setOpen] = useState(false);
    const [ended, setEnded] = useState(false);
    const [endLeague, { data, error }] = useMutation(END_LEAGUE_MUTATION);
    const handleClickOpen = () => {
      setOpen(true);
    };

    const handleClose = () => {
      setOpen(false);
    };
    const handleEndLeague = async () => {
      await endLeague({
        variables: {
          leagueId: team.league.id,
        },
      });
    };

    useEffect(() => {
      if (data) {
        setEnded(data.endLeague);
        setOpen(false);
      }
    }, [data, error]);

    return (
      <>
        {useIsManager(currentUser.role) && (
          <CardWrapper data-testid={`group-summary-${id}`}>
            <Header>
              <Row>
                <Grid item xs={8}>
                  {!team.league && (
                    <Title to="/">{intl.formatMessage(messages.league)}</Title>
                  )}
                  {team.league && (
                    <Title to={`/leagues/${team.league.id}`}>
                      <span>
                        {intl.formatMessage(messages.league)}: {team.league.name}
                      </span>
                      <Chevron
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="9 6 15 12 9 18" />
                      </Chevron>
                    </Title>
                  )}
                </Grid>
              </Row>
            </Header>
            <Content>
              {!team.league && !ended && (
                <Grid container>
                  <Grid item xs={12}>
                    <Typography.Subtitle1>
                      {intl.formatMessage(messages.noLeagueTitle)}
                    </Typography.Subtitle1>
                  </Grid>
                  <Grid item xs={12}>
                    <ActionLink to={`/leagues/${team.id}/addLeague`}>
                      <Button color="primary">
                        {intl.formatMessage(messages.addLeague)}
                      </Button>
                    </ActionLink>
                  </Grid>
                </Grid>
              )}
              {team.league && (
                <>
                  <Dialog
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                  >
                    <DialogTitle id="alert-dialog-title">
                      {intl.formatMessage(messages.confirmEndLeagueTitle)}
                    </DialogTitle>
                    <DialogContent>
                      <DialogContentText id="alert-dialog-description">
                        {intl.formatMessage(messages.confirmEndLeagueMessage)}
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={handleEndLeague} color="primary">
                        {intl.formatMessage(messages.confirmEndLeagueOK)}
                      </Button>
                      <Button onClick={handleClose} color="primary" autoFocus>
                        {intl.formatMessage(messages.confirmEndLeagueKO)}
                      </Button>
                    </DialogActions>
                  </Dialog>
                  <Grid container>
                    <Grid item xs={6}>
                      <DetailLabel>{intl.formatMessage(messages.leagueLocation)}</DetailLabel>
                      <DetailValue>{team.league.location}</DetailValue>
                    </Grid>
                    <Grid item xs={6}>
                      <DetailLabel>{intl.formatMessage(messages.leagueStartDate)}</DetailLabel>
                      <DetailValue>{team.league.startDate}</DetailValue>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      style={{ marginTop: 8, marginBottom: 8 }}
                    >
                      <Button variant="outlined" onClick={handleClickOpen}>
                        {intl.formatMessage(messages.endLeague)}
                      </Button>
                    </Grid>
                  </Grid>
                </>
              )}
            </Content>
          </CardWrapper>
        )}
        <SnackBar
          data-testid="error-message"
          open={ended}
          // message={intl.formatMessage(messages.errorUpdatingPassword)}
          message="League deleted"
          variant="success"
          actionText="close"
          action={handleClose}
        />
      </>
    );
  }
);

export default LeagueCard;
